import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { 
  Settings, Save, Loader2, Trash2, AlertTriangle, Globe, Lock, 
  Users, Share2, UserPlus, Download, FileText, X, Copy, Check
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE } from '../../config/api';
const API = `${API_BASE}`;

const SettingsPage = () => {
  const { conlang, setConlang, fetchConlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  const navigate = useNavigate();
  
  const [name, setName] = useState(conlang?.name || '');
  const [nativeName, setNativeName] = useState(conlang?.native_name || '');
  const [description, setDescription] = useState(conlang?.description || '');
  const [visibility, setVisibility] = useState(conlang?.visibility || 'private');
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Sharing
  const [shares, setShares] = useState([]);
  const [friends, setFriends] = useState([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [accessLevel, setAccessLevel] = useState('read');
  const [shareLoading, setShareLoading] = useState(false);
  
  // Export
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [fullExporting, setFullExporting] = useState(false);
  const [exportSections, setExportSections] = useState({
    overview: true,
    phonology: true,
    phonotactics: true,
    lexicon: true,
    morphology: true,
    syntax: true
  });
  const [exportFormat, setExportFormat] = useState('markdown');
  const [exporting, setExporting] = useState(false);
  const [exportContent, setExportContent] = useState(null);
  const [copied, setCopied] = useState(false);

  const isOwner = conlang?.access_level === 'owner' || conlang?.user_id === conlang?.user_id;

  useEffect(() => {
    if (isOwner) {
      fetchShares();
      fetchFriends();
    }
  }, [conlangId, isOwner]);

  const fetchShares = async () => {
    try {
      const response = await axios.get(`${API}/conlangs/${conlangId}/shares`, { withCredentials: true });
      setShares(response.data);
    } catch (error) {
      console.error('Failed to load shares');
    }
  };

  const fetchFriends = async () => {
    try {
      const response = await axios.get(`${API}/friends`, { withCredentials: true });
      setFriends(response.data);
    } catch (error) {
      console.error('Failed to load friends');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/conlangs/${conlang.conlang_id}`,
        { name, native_name: nativeName, description },
        { withCredentials: true }
      );
      setConlang(response.data);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleVisibilityChange = async (newVisibility) => {
    try {
      await axios.put(
        `${API}/conlangs/${conlangId}/visibility`,
        { visibility: newVisibility },
        { withCredentials: true }
      );
      setVisibility(newVisibility);
      fetchConlang();
      toast.success(`Language is now ${newVisibility}`);
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const handleShare = async () => {
    if (!selectedFriend) return;
    setShareLoading(true);
    try {
      await axios.post(
        `${API}/conlangs/${conlangId}/shares`,
        { user_id: selectedFriend, access_level: accessLevel },
        { withCredentials: true }
      );
      toast.success('Language shared!');
      setShareDialogOpen(false);
      setSelectedFriend(null);
      fetchShares();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    } finally {
      setShareLoading(false);
    }
  };

  const handleRemoveShare = async (userId, userName) => {
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/shares/${userId}`, { withCredentials: true });
      toast.success(`Removed ${userName}'s access`);
      fetchShares();
    } catch (error) {
      toast.error('Failed to remove share');
    }
  };

  const handleUpdateAccess = async (userId, newLevel) => {
    try {
      await axios.put(
        `${API}/conlangs/${conlangId}/shares/${userId}`,
        { user_id: userId, access_level: newLevel },
        { withCredentials: true }
      );
      toast.success('Access level updated');
      fetchShares();
    } catch (error) {
      toast.error('Failed to update access');
    }
  };

  const handleFullExport = async () => {
    setFullExporting(true);
    try {
      const response = await axios.get(
        `${API}/conlangs/${conlangId}/export-full`,
        { withCredentials: true }
      );
      const blob = new Blob([JSON.stringify(response.data.content, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename || `${conlang?.name}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Full export downloaded');
    } catch {
      toast.error('Full export failed');
    } finally {
      setFullExporting(false);
    }
  };

  const handleExport = async () => {
    const selectedSections = Object.entries(exportSections)
      .filter(([_, selected]) => selected)
      .map(([section]) => section);
    
    if (selectedSections.length === 0) {
      toast.error('Select at least one section to export');
      return;
    }
    
    setExporting(true);
    try {
      const response = await axios.post(
        `${API}/conlangs/${conlangId}/export`,
        { sections: selectedSections, format: exportFormat },
        { withCredentials: true }
      );
      setExportContent(response.data);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDownload = () => {
    if (!exportContent) return;
    const blob = new Blob([
      exportFormat === 'markdown' ? exportContent.content : JSON.stringify(exportContent.content, null, 2)
    ], { type: exportFormat === 'markdown' ? 'text/markdown' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportContent.filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded!');
  };

  const handleCopy = async () => {
    if (!exportContent) return;
    await navigator.clipboard.writeText(
      exportFormat === 'markdown' ? exportContent.content : JSON.stringify(exportContent.content, null, 2)
    );
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/conlangs/${conlang.conlang_id}`, { withCredentials: true });
      toast.success('Language deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete language');
      setDeleting(false);
    }
  };

  // Friends not yet shared with
  const availableFriends = friends.filter(f => 
    !shares.find(s => s.user_id === f.user_id)
  );

  return (
    <div className="max-w-2xl space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="font-heading text-3xl font-bold text-foreground" data-testid="settings-title">
          Settings
        </h2>
        <p className="text-foreground-muted mt-1">
          Manage your language settings
        </p>
      </div>

      {/* General Settings */}
      <Card className="bg-background-paper border-border" data-testid="general-settings-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-foreground flex items-center gap-2">
            <Settings className="w-5 h-5" />
            General
          </CardTitle>
          <CardDescription className="text-foreground-muted">
            Basic information about your language
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground">Language Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Elvish" className="bg-background border-border" required data-testid="settings-name-input" disabled={!isOwner} />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="nativeName" className="text-foreground">Native Name</Label>
              <Input id="nativeName" value={nativeName} onChange={(e) => setNativeName(e.target.value)} placeholder="e.g., Quendya" className="bg-background border-border font-mono" data-testid="settings-native-name-input" disabled={!isOwner} />
              <p className="text-xs text-foreground-subtle">The name of the language in the language itself</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground">Short Description</Label>
              <Input 
                id="description" 
                value={description} 
                onChange={(e) => setDescription(e.target.value.slice(0, 60))} 
                placeholder="A brief tagline for your language..." 
                className="bg-background border-border" 
                maxLength={60}
                data-testid="settings-description-input" 
                disabled={!isOwner} 
              />
              <p className="text-xs text-foreground-subtle text-right">{description.length}/60</p>
            </div>

            {isOwner && (
              <div className="flex justify-end">
                <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="settings-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Visibility & Sharing - Only for owners */}
      {isOwner && (
        <Card className="bg-background-paper border-border" data-testid="sharing-settings-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-foreground flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Visibility & Sharing
            </CardTitle>
            <CardDescription className="text-foreground-muted">
              Control who can see and edit your language
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Visibility Toggle */}
            <div className="flex items-center justify-between p-4 rounded-md border border-border bg-background">
              <div className="flex items-center gap-3">
                {visibility === 'public' ? (
                  <Globe className="w-5 h-5 text-secondary" />
                ) : (
                  <Lock className="w-5 h-5 text-foreground-muted" />
                )}
                <div>
                  <p className="font-medium text-foreground">Public Language</p>
                  <p className="text-sm text-foreground-muted">
                    {visibility === 'public' 
                      ? 'Anyone can view this language in Discover' 
                      : 'Only you and people you share with can view'}
                  </p>
                </div>
              </div>
              <Switch 
                checked={visibility === 'public'} 
                onCheckedChange={(checked) => handleVisibilityChange(checked ? 'public' : 'private')}
                data-testid="visibility-toggle"
              />
            </div>

            {/* Allow Cloning Toggle (only when public) */}
            {visibility === 'public' && (
              <div className="flex items-center justify-between p-4 rounded-md border border-border bg-background" data-testid="allow-cloning-card">
                <div className="flex items-center gap-3">
                  <Copy className="w-5 h-5 text-foreground-muted" />
                  <div>
                    <p className="font-medium text-foreground">Allow Cloning</p>
                    <p className="text-sm text-foreground-muted">
                      {conlang?.allow_cloning ? 'Anyone can clone this language' : 'Only shared users with clone permission can clone'}
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={conlang?.allow_cloning || false}
                  onCheckedChange={async (checked) => {
                    try {
                      await axios.put(`${API}/conlangs/${conlangId}/allow-cloning?allow=${checked}`, {}, { withCredentials: true });
                      fetchConlang();
                      toast.success(checked ? 'Cloning enabled' : 'Cloning disabled');
                    } catch { toast.error('Failed to update'); }
                  }}
                  data-testid="allow-cloning-toggle"
                />
              </div>
            )}

            {/* Share with Friends */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-foreground-muted" />
                  <span className="font-medium text-foreground">Shared With</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShareDialogOpen(true)}
                  disabled={availableFriends.length === 0}
                  className="border-border"
                  data-testid="share-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>

              {shares.length > 0 ? (
                <div className="space-y-2" data-testid="shares-list">
                  {shares.map(share => (
                    <div key={share.user_id} className="flex items-center justify-between p-3 rounded-md border border-border bg-background">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-primary text-sm font-medium">{share.name.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-foreground text-sm font-medium">{share.name}</p>
                          <p className="text-foreground-muted text-xs">{share.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={share.access_level} onValueChange={(v) => handleUpdateAccess(share.user_id, v)}>
                          <SelectTrigger className="w-24 h-8 text-xs bg-background border-border">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background-paper border-border">
                            <SelectItem value="read">View Only</SelectItem>
                            <SelectItem value="clone">Clone</SelectItem>
                            <SelectItem value="collaborate">Collaborate</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveShare(share.user_id, share.name)} className="h-8 w-8 p-0 text-foreground-muted hover:text-destructive">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-foreground-muted text-sm text-center py-4">
                  Not shared with anyone yet. {availableFriends.length === 0 && 'Add friends first!'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export */}
      <Card className="bg-background-paper border-border" data-testid="export-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-foreground flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export
          </CardTitle>
          <CardDescription className="text-foreground-muted">
            Download your language documentation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={() => setExportDialogOpen(true)} variant="outline" className="border-border" data-testid="export-btn">
            <FileText className="w-4 h-4 mr-2" />
            Export Documentation
          </Button>
          <Button onClick={handleFullExport} disabled={fullExporting} variant="outline" className="border-border ml-2" data-testid="full-export-btn">
            {fullExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Full Export (JSON)
          </Button>
          <p className="text-xs text-foreground-subtle">
            Full Export creates a portable JSON file that can be imported into another account.
          </p>
        </CardContent>
      </Card>

      {/* Danger Zone - Only for owners */}
      {isOwner && (
        <Card className="bg-background-paper border-destructive/50" data-testid="danger-zone-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-destructive flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription className="text-foreground-muted">Irreversible actions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-md border border-destructive/30 bg-destructive/5">
              <div>
                <p className="font-medium text-foreground">Delete this language</p>
                <p className="text-sm text-foreground-muted">Permanently delete {conlang?.name} and all its data</p>
              </div>
              {canEdit && (
              <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-language-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="bg-background-paper border-border" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Share Language</DialogTitle>
            <DialogDescription className="text-foreground-muted">Share with a friend</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Select Friend</Label>
              <Select value={selectedFriend || ''} onValueChange={setSelectedFriend}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Choose a friend..." />
                </SelectTrigger>
                <SelectContent className="bg-background-paper border-border">
                  {availableFriends.map(f => (
                    <SelectItem key={f.user_id} value={f.user_id}>{f.name} ({f.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Access Level</Label>
              <Select value={accessLevel} onValueChange={setAccessLevel}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background-paper border-border">
                  <SelectItem value="read">Viewer (read only)</SelectItem>
                  <SelectItem value="edit">Co-creator (can edit)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleShare} disabled={!selectedFriend || shareLoading} className="bg-primary hover:bg-primary/90">
              {shareLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Share'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={(open) => { setExportDialogOpen(open); if (!open) setExportContent(null); }}>
        <DialogContent className="bg-background-paper border-border max-w-lg" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Export Documentation</DialogTitle>
            <DialogDescription className="text-foreground-muted">Select sections to include</DialogDescription>
          </DialogHeader>
          
          {!exportContent ? (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Sections</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries({
                      overview: 'Overview',
                      phonology: 'Phonology',
                      phonotactics: 'Phonotactics',
                      lexicon: 'Lexicon',
                      morphology: 'Morphology',
                      syntax: 'Syntax'
                    }).map(([key, label]) => (
                      <div key={key} className="flex items-center space-x-2">
                        <Checkbox 
                          id={key} 
                          checked={exportSections[key]}
                          onCheckedChange={(checked) => setExportSections({...exportSections, [key]: checked})}
                        />
                        <label htmlFor={key} className="text-sm text-foreground cursor-pointer">{label}</label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Format</Label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger className="bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background-paper border-border">
                      <SelectItem value="markdown">Markdown (.md)</SelectItem>
                      <SelectItem value="json">JSON (.json)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setExportDialogOpen(false)} className="border-border">Cancel</Button>
                <Button onClick={handleExport} disabled={exporting} className="bg-primary hover:bg-primary/90">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Generate
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="bg-background border border-border rounded-md p-4 max-h-[300px] overflow-auto">
                <pre className="text-xs text-foreground-muted whitespace-pre-wrap font-mono">
                  {exportFormat === 'markdown' 
                    ? exportContent.content.substring(0, 2000) + (exportContent.content.length > 2000 ? '...' : '')
                    : JSON.stringify(exportContent.content, null, 2).substring(0, 2000)
                  }
                </pre>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCopy} className="border-border">
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button onClick={handleDownload} className="bg-primary hover:bg-primary/90">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-background-paper border-border" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete {conlang?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-foreground-muted">
              This action cannot be undone. This will permanently delete your language and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive hover:bg-destructive/90" data-testid="delete-confirm-btn">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Language
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SettingsPage;
